import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Avatar from '../components/Avatar.jsx'
import ConfirmDialog from '../components/ConfirmDialog.jsx'
import Field from '../components/Field.jsx'
import FormAlert from '../components/FormAlert.jsx'
import HiddenUsername from '../components/HiddenUsername.jsx'
import InlineAlert from '../components/InlineAlert.jsx'
import QrCode from '../components/QrCode.jsx'
import Toggle from '../components/Toggle.jsx'
import { useAuth } from '../context/AuthContext.jsx'
import { useTheme } from '../context/ThemeContext.jsx'
import { api, ApiError } from '../lib/api.js'
import { downscaleImage } from '../lib/downscaleImage.js'
import { plural } from '../lib/format.js'

const THEME_LABELS = { system: 'System', light: 'Light', dark: 'Dark' }

// PLAN §12: "User-preferred service in settings." These are search links, not
// account connections — Sonologue holds no token for any of them.
const MUSIC_SERVICES = [
  ['spotify', 'Spotify'],
  ['apple_music', 'Apple Music'],
  ['youtube_music', 'YouTube Music'],
]

/**
 * What can be muted, and where. The two scopes answer different questions —
 * "keep this out of my timeline" is not "do not tell me about this" — and the
 * screen shows them side by side because a person thinks of them as one list.
 *
 * These strings are `reviews_social.events.MUTABLE_EVENT_TYPES` and
 * `notifications.models.NOTIFICATION_TYPES`. A type the server does not know
 * is a row that does nothing; a type missing here is a setting nobody can
 * reach. Both are visible in one screenshot, which is the check.
 */
const FEED_EVENTS = [
  ['rating_published', 'Ratings'],
  ['review_published', 'Reviews'],
  ['list_published', 'Lists'],
  ['completed_album', 'Finished albums'],
]

const NOTIFICATION_EVENTS = [
  ['follow', 'New followers'],
  ['follow_request', 'Follow requests'],
  // W9d (Q34): one type, `comment`, governs list comments too.
  ['comment', 'Comments on your reviews and lists'],
  ['reaction', 'Hearts'],
  ['tag_adopted', 'A tag you suggested'],
  ['tag_aliased', 'A tag you suggested, under another name'],
]

/**
 * Achievements, as words (1B-4b Phase 2b, DEC-041).
 *
 * The server stores `tag_contributor` and the client says what that means —
 * the same split as a tag's slug and its label, and for the same reason:
 * rewording a badge should not be a migration.
 */
const ACHIEVEMENT_LABELS = { tag_contributor: 'Tag contributor' }
const ACHIEVEMENT_HELP = { tag_contributor: 'A word you suggested is in the tag list. Since' }

/**
 * A user-agent string, as something a person can recognise.
 *
 * Deliberately shallow: the list only has to be good enough to tell your own
 * devices apart, and a full parser would be a dependency plus a maintenance
 * commitment for a line of text. Anything unrecognised falls back to the raw
 * string rather than to "Unknown" — the raw string is ugly but it is evidence,
 * and this is the screen where somebody is deciding whether a login was theirs.
 */
export function describeDevice(ua) {
  if (!ua) return 'Unknown device'
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : null
  const platform =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'Mac'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : null
  if (browser && platform) return `${browser} on ${platform}`
  return browser || platform || ua
}

function Section({ title, description, children }) {
  return (
    <section className="mb-8">
      <div className="section-header">
        <h2 className="title-3">{title}</h2>
      </div>
      {description ? <p className="page-intro">{description}</p> : null}
      <div className="card card-pad">{children}</div>
    </section>
  )
}

export default function Settings() {
  const { user, updateMe, setAvatar, removeAvatar, logout } = useAuth()
  const { theme, setTheme, choices } = useTheme()
  const navigate = useNavigate()
  const avatarInput = useRef(null)

  const [profile, setProfile] = useState({
    display_name: user.display_name,
    bio: user.bio,
    username: user.username,
  })
  // Blind mode lives in `ratings`, not on `/me`, because the rule it feeds
  // needs to know what you have rated and `identity` sits below `ratings`.
  // So it is fetched on its own rather than arriving with the user object.
  const [blindMode, setBlindMode] = useState(false)
  // A failed load used to leave the switch reading off whatever the server
  // held, which is a setting lying about itself. Same shape as `loadInvites`
  // below, so *Try again* has something to call.
  const [blindFailed, setBlindFailed] = useState(false)
  const loadBlind = useCallback(() => {
    api
      .blindMode()
      .then((body) => {
        setBlindMode(Boolean(body.enabled))
        setBlindFailed(false)
      })
      .catch(() => setBlindFailed(true))
  }, [])

  useEffect(loadBlind, [loadBlind])

  // The switch's own sentence, in the slot the failed-load alert already uses.
  const [blindError, setBlindError] = useState('')

  async function setBlind(next) {
    setBlindError('')
    setBlindMode(next)
    try {
      const body = await api.setBlindMode(next)
      setBlindMode(Boolean(body.enabled))
    } catch (err) {
      // The revert stays — and now it says why it happened, which is the
      // difference between a switch that snapped back and one that lied.
      setBlindMode(!next)
      setBlindError(err instanceof ApiError ? err.message : 'Could not save that just now.')
    }
  }

  // What this account has earned (1B-4b Phase 2b). Its own request, and only
  // here — the owner put these on Settings and not on a public profile, and
  // there is no route that reads somebody else's.
  const [achievements, setAchievements] = useState([])
  useEffect(() => {
    const controller = new AbortController()
    api
      .achievements({ signal: controller.signal })
      .then((body) => setAchievements(body.items || []))
      .catch(() => {})
    return () => controller.abort()
  }, [])

  const [profileState, setProfileState] = useState({ alert: null, tone: 'caution', busy: false })
  const [avatarState, setAvatarState] = useState({ alert: null, tone: 'caution', busy: false })
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '' })
  const [passwordState, setPasswordState] = useState({ alert: null, tone: 'caution', busy: false })
  const [verifyNote, setVerifyNote] = useState(null)
  const [invites, setInvites] = useState(null)
  const [invitesFailed, setInvitesFailed] = useState(false)
  const [safety, setSafety] = useState(null)
  const [safetyFailed, setSafetyFailed] = useState(false)
  const [devices, setDevices] = useState(null)
  const [deviceState, setDeviceState] = useState({ error: '', busy: '' })
  // Two-factor (1B-4a). `twoFactor` is the server's answer — including
  // `available`, which is the `auth.totp` flag; `enrolment` is the secret and
  // QR of a setup in progress, held only while the panel is open; and
  // `recoveryCodes` is shown exactly once, because there is no route that
  // returns them a second time.
  const [twoFactor, setTwoFactor] = useState(null)
  const [twoFactorFailed, setTwoFactorFailed] = useState(false)
  const [enrolment, setEnrolment] = useState(null)
  const [twoFactorForm, setTwoFactorForm] = useState({ password: '', code: '' })
  const [twoFactorState, setTwoFactorState] = useState({ alert: null, tone: 'caution', busy: false })
  const [recoveryCodes, setRecoveryCodes] = useState(null)
  const [codesSaved, setCodesSaved] = useState(false)
  const [codesCopied, setCodesCopied] = useState(false)
  const [privacy, setPrivacy] = useState(null)
  const [privacyError, setPrivacyError] = useState('')
  const [exporting, setExporting] = useState(false)
  const [wiping, setWiping] = useState({ confirming: false, busy: false, done: false })
  const [wipeError, setWipeError] = useState('')
  const [deleting, setDeleting] = useState({
    open: false,
    mode: 'delete',
    password: '',
    busy: false,
    error: '',
  })

  useEffect(() => {
    document.title = 'Settings — Sonologue'
  }, [])

  const loadInvites = useCallback(() => {
    api
      .invites()
      .then((rows) => {
        setInvites(rows)
        setInvitesFailed(false)
      })
      .catch(() => {
        setInvites([])
        setInvitesFailed(true)
      })
  }, [])

  useEffect(loadInvites, [loadInvites])

  const loadSafety = useCallback(() => {
    api
      .safety()
      .then((body) => {
        setSafety(body)
        setSafetyFailed(false)
      })
      .catch(() => {
        // The empty shape stays, and the flag goes beside it. `setEventMute`
        // spreads `safety` and the render reads `safety.blocked`, so leaving
        // this null would turn a rare crash into a certain one.
        setSafety({ blocked: [], muted: [], follow_requests: [], event_mutes: {} })
        setSafetyFailed(true)
      })
  }, [])

  useEffect(loadSafety, [loadSafety])

  const loadDevices = useCallback(() => {
    setDeviceState((s) => ({ ...s, error: '' }))
    api
      .sessions()
      .then((data) => setDevices(data.sessions))
      .catch(() => {
        setDevices([])
        setDeviceState((s) => ({ ...s, error: 'Your signed-in devices did not load.' }))
      })
  }, [])

  useEffect(loadDevices, [loadDevices])

  const loadTwoFactor = useCallback(() => {
    api
      .twoFactor()
      .then((body) => {
        setTwoFactor(body)
        setTwoFactorFailed(false)
      })
      .catch(() => {
        // Same shape as the other failed loads: say so rather than render a
        // panel that claims two-factor is off when nobody knows.
        setTwoFactor(null)
        setTwoFactorFailed(true)
      })
  }, [])

  useEffect(loadTwoFactor, [loadTwoFactor])

  async function startTwoFactor(event) {
    event.preventDefault()
    setTwoFactorState({ alert: null, tone: 'caution', busy: true })
    try {
      const body = await api.startTwoFactor({ password: twoFactorForm.password })
      setEnrolment(body)
      setTwoFactorForm({ password: '', code: '' })
      setTwoFactorState({ alert: null, tone: 'caution', busy: false })
    } catch (error) {
      setTwoFactorState({
        alert: error instanceof ApiError ? error.message : 'Could not start setting that up.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function confirmTwoFactor(event) {
    event.preventDefault()
    setTwoFactorState({ alert: null, tone: 'caution', busy: true })
    try {
      const body = await api.confirmTwoFactor({ code: twoFactorForm.code })
      setRecoveryCodes(body.recovery_codes)
      setCodesSaved(false)
      setCodesCopied(false)
      setEnrolment(null)
      setTwoFactorForm({ password: '', code: '' })
      setTwoFactorState({
        alert:
          body.sessions_revoked > 0
            ? 'Two-factor is on. Your other devices were signed out. We have emailed you to say so.'
            : 'Two-factor is on. We have emailed you to say so.',
        tone: 'positive',
        busy: false,
      })
      loadTwoFactor()
    } catch (error) {
      setTwoFactorState({
        alert: error instanceof ApiError ? error.message : 'Could not turn that on.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function turnOffTwoFactor(event) {
    event.preventDefault()
    setTwoFactorState({ alert: null, tone: 'caution', busy: true })
    try {
      await api.disableTwoFactor({ password: twoFactorForm.password })
      setTwoFactorForm({ password: '', code: '' })
      setRecoveryCodes(null)
      setTwoFactorState({
        alert: 'Two-factor is off. We have emailed you to say so.',
        tone: 'positive',
        busy: false,
      })
      loadTwoFactor()
    } catch (error) {
      setTwoFactorState({
        alert: error instanceof ApiError ? error.message : 'Could not turn that off.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function copyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCodesCopied(true)
    } catch {
      // A refused clipboard is not an error worth a banner — the codes are on
      // screen and can be selected.
      setCodesCopied(false)
    }
  }

  async function endDevice(handle) {
    setDeviceState({ error: '', busy: handle })
    try {
      await api.revokeSession(handle)
      loadDevices()
    } catch (error) {
      setDeviceState({
        error: error instanceof ApiError ? error.message : 'That device could not be signed out.',
        busy: '',
      })
      return
    }
    setDeviceState({ error: '', busy: '' })
  }

  async function endOtherDevices() {
    setDeviceState({ error: '', busy: 'others' })
    try {
      await api.revokeOtherSessions()
      loadDevices()
    } catch (error) {
      setDeviceState({
        error: error instanceof ApiError ? error.message : 'Those devices could not be signed out.',
        busy: '',
      })
      return
    }
    setDeviceState({ error: '', busy: '' })
  }

  const loadPrivacy = useCallback(() => {
    setPrivacyError('')
    api
      .privacy()
      .then(setPrivacy)
      .catch(() => setPrivacyError('Your data settings did not load.'))
  }, [])

  useEffect(loadPrivacy, [loadPrivacy])

  async function startExport() {
    setExporting(true)
    setPrivacyError('')
    try {
      setPrivacy(await api.requestExport())
    } catch (error) {
      setPrivacyError(
        error instanceof ApiError ? error.message : 'Could not start that export.',
      )
    } finally {
      setExporting(false)
    }
  }

  async function confirmDeletion() {
    setDeleting((d) => ({ ...d, busy: true, error: '' }))
    try {
      await api.requestDeletion({ password: deleting.password, content_mode: deleting.mode })
      // Every session was just revoked, this one included. Going to the
      // farewell page rather than reloading avoids a flash of a signed-in
      // shell that no longer has a session behind it.
      navigate('/account/deleted', { replace: true })
    } catch (error) {
      setDeleting((d) => ({
        ...d,
        busy: false,
        // The highest-stakes action in the product: what a person needs at
        // this moment is which side of it they are on.
        error:
          error instanceof ApiError
            ? error.message
            : 'Sonologue didn’t answer. Nothing was deleted — try again in a moment.',
      }))
    }
  }

  // One sentence per toggle, and one per person in the lists below. Ten
  // switches and three near-identical row blocks share these two maps rather
  // than ten and three copies of the same catch.
  const [muteErrors, setMuteErrors] = useState({})
  const [safetyRowErrors, setSafetyRowErrors] = useState({})

  async function setEventMute(scope, eventType, muted) {
    const key = `${scope}:${eventType}`
    setMuteErrors((current) => ({ ...current, [key]: '' }))
    try {
      const next = await api.setEventMute({ scope, event_type: eventType, muted })
      setSafety((current) => ({ ...current, event_mutes: next }))
    } catch (err) {
      // The switch keeps its true state: nothing moved before the answer.
      setMuteErrors((current) => ({
        ...current,
        [key]: err instanceof ApiError ? err.message : 'Could not save that just now.',
      }))
    }
  }

  /** A blocked, muted or requesting person's row: act, then reload on success. */
  async function safetyRowAction(key, call, fallback) {
    setSafetyRowErrors((current) => ({ ...current, [key]: '' }))
    try {
      await call()
      loadSafety()
    } catch (err) {
      // `loadSafety()` on success only — the list is unchanged, so re-reading
      // it would cost a request and tell the person nothing.
      setSafetyRowErrors((current) => ({
        ...current,
        [key]: err instanceof ApiError ? err.message : fallback,
      }))
    }
  }

  async function uploadAvatar(file) {
    setAvatarState({ alert: null, tone: 'caution', busy: true })
    try {
      // Shrunk here, not on the server. `avatars.py` never decodes an image on
      // purpose, and a phone photo is otherwise stored and served in full to
      // draw a 28-pixel circle. A file already small enough comes back
      // untouched.
      await setAvatar(await downscaleImage(file))
      setAvatarState({ alert: 'Picture updated.', tone: 'positive', busy: false })
    } catch (error) {
      setAvatarState({
        // The API's own message names what was wrong with the file — the
        // format, the size, the dimensions — and it is better than anything
        // this screen could guess at.
        alert: error instanceof ApiError ? error.message : 'Could not upload that picture.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function dropAvatar() {
    setAvatarState({ alert: null, tone: 'caution', busy: true })
    try {
      await removeAvatar()
      setAvatarState({ alert: 'Picture removed.', tone: 'positive', busy: false })
    } catch (error) {
      setAvatarState({
        alert: error instanceof ApiError ? error.message : 'Could not remove that picture.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function saveProfile(event) {
    event.preventDefault()
    setProfileState({ alert: null, tone: 'caution', busy: true })
    try {
      await updateMe(profile)
      setProfileState({ alert: 'Saved.', tone: 'positive', busy: false })
    } catch (error) {
      setProfileState({
        alert: error instanceof ApiError ? error.message : 'Could not save.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function savePassword(event) {
    event.preventDefault()
    setPasswordState({ alert: null, tone: 'caution', busy: true })
    try {
      await api.changePassword(passwords)
      setPasswords({ current_password: '', new_password: '' })
      setPasswordState({
        alert: 'Password changed. Your other devices were signed out.',
        tone: 'positive',
        busy: false,
      })
    } catch (error) {
      setPasswordState({
        alert: error instanceof ApiError ? error.message : 'Could not change your password.',
        tone: 'caution',
        busy: false,
      })
    }
  }

  async function resendVerification() {
    try {
      await api.requestEmailVerification()
      setVerifyNote('Sent. Check your email for the link.')
    } catch (error) {
      setVerifyNote(error instanceof ApiError ? error.message : 'Could not send it.')
    }
  }

  async function signOut() {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <>
      <div className="section-header">
        <h1 className="large-title">Settings</h1>
      </div>

      <Section title="Profile">
        <FormAlert tone={profileState.tone}>{profileState.alert}</FormAlert>

        <div className="avatar-control">
          <Avatar name={user.display_name || user.username} avatarUrl={user.avatar_url} size={72} />
          <div className="avatar-actions">
            <div className="row-title">Profile picture</div>
            <div className="row-sub wrap">
              PNG, JPEG or WebP, up to 2 MB. Square looks best — anything else is cropped to the
              middle.
            </div>
            {/* The native control renders as a grey "Choose file" box that no
                design token reaches, so the button is the control and this is
                the mechanism behind it. `hidden` rather than clipped: a
                programmatic click opens the chooser either way, and a hidden
                input is out of the accessibility tree instead of being an
                unlabelled field in it. */}
            <input
              ref={avatarInput}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) uploadAvatar(file)
              }}
            />
            <div className="avatar-buttons">
              <button
                type="button"
                className="btn btn-tonal btn-sm"
                disabled={avatarState.busy}
                onClick={() => avatarInput.current?.click()}
              >
                {avatarState.busy ? 'Uploading…' : user.avatar_url ? 'Replace' : 'Upload'}
              </button>
              {user.avatar_url ? (
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  disabled={avatarState.busy}
                  onClick={dropAvatar}
                >
                  Remove
                </button>
              ) : null}
            </div>
          </div>
        </div>
        <FormAlert tone={avatarState.tone}>{avatarState.alert}</FormAlert>

        <form onSubmit={saveProfile} noValidate>
          <Field
            label="Display name"
            value={profile.display_name}
            onChange={(e) => setProfile((p) => ({ ...p, display_name: e.target.value }))}
            maxLength={60}
          />
          <Field
            label="Username"
            value={profile.username}
            onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))}
            hint="Your profile address. Changing it breaks old links."
            maxLength={30}
          />
          <Field
            label="Bio"
            value={profile.bio}
            onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
            maxLength={500}
          />
          <button type="submit" className="btn btn-primary" disabled={profileState.busy}>
            {profileState.busy ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </Section>

      <Section title="Appearance">
        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title">Theme</div>
            <div className="row-sub wrap">Follows your system unless you pick one.</div>
          </div>
          <div className="segmented" role="group" aria-label="Theme">
            {choices.map((choice) => (
              <button
                key={choice}
                type="button"
                aria-pressed={theme === choice}
                onClick={() => setTheme(choice)}
              >
                {THEME_LABELS[choice]}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Rating">
        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title" id="bonus-label">
              Include bonus tracks in your track average
            </div>
            <div className="row-sub wrap" id="bonus-help">
              Deluxe extras and bonus discs are always rateable. This is whether their scores pull
              the average Sonologue computes for the album. Half of the standard tracklist still
              has to be rated before that average is shown.
            </div>
          </div>
          <Toggle
            checked={user.include_bonus_in_track_average}
            onChange={(next) => updateMe({ include_bonus_in_track_average: next })}
            label="Include bonus tracks in your track average"
            describedBy="bonus-help"
          />
        </div>
      </Section>

      <Section title="Listening">
        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title">Preferred service</div>
            <div className="row-sub wrap">
              Listed first on album and song pages. Sonologue links to a search there; it holds no
              account of yours.
            </div>
          </div>
          <div className="segmented" role="group" aria-label="Preferred music service">
            {MUSIC_SERVICES.map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={user.preferred_music_service === value}
                onClick={() => updateMe({ preferred_music_service: value })}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <Section title="Blind rating">
        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title">Hide other scores until I rate</div>
            <div className="row-sub wrap" id="blind-help">
              Community averages, distributions, friends’ scores and the numbers on reviews stay
              hidden on anything you have not rated yet. Rating it reveals them. Individual albums
              and songs can disagree with this from their own pages.
            </div>
          </div>
          <Toggle
            checked={Boolean(blindMode)}
            onChange={setBlind}
            label="Hide other scores until I rate"
            describedBy="blind-help"
          />
        </div>
        {/* After the row, not inside it: `.form-row` is flex, so a third
            child would sit beside the switch. */}
        {blindFailed ? (
          <InlineAlert>
            This setting did not load.{' '}
            <button type="button" className="link-btn" onClick={loadBlind}>
              Try again
            </button>
          </InlineAlert>
        ) : null}
        <InlineAlert>{blindError}</InlineAlert>
      </Section>

      {/*
        Nothing at all when there is nothing to show. An empty "Contributions"
        section would be a permanent reminder of a thing most people will never
        do, on a screen they came to for something else.
      */}
      {achievements.length ? (
        <Section title="Contributions">
          {achievements.map((row) => (
            <div className="form-row p-0" key={row.name}>
              <div className="row-main">
                <div className="row-title">{ACHIEVEMENT_LABELS[row.name] || row.name}</div>
                <div className="row-sub wrap">
                  {ACHIEVEMENT_HELP[row.name] || ''}{' '}
                  {new Date(row.awarded_at).toLocaleDateString()}
                </div>
              </div>
            </div>
          ))}
        </Section>
      ) : null}

      <Section title="Privacy">
        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title">Private account</div>
            <div className="row-sub wrap" id="private-help">
              Approve followers before they see your ratings. Enforced everywhere from the social
              pass onward.
            </div>
          </div>
          <Toggle
            checked={user.is_private}
            onChange={(next) => updateMe({ is_private: next })}
            label="Private account"
            describedBy="private-help"
          />
        </div>
      </Section>

      <Section title="Account">
        <div className="form-row p-0 mb-[18px]">
          <div className="row-main">
            <div className="row-title">{user.email}</div>
            <div className="row-sub">
              {user.email_verified ? 'Confirmed.' : verifyNote || 'Not confirmed yet.'}
            </div>
          </div>
          {!user.email_verified ? (
            <button type="button" className="btn btn-quiet btn-sm" onClick={resendVerification}>
              Send link
            </button>
          ) : null}
        </div>

        <FormAlert tone={passwordState.tone}>{passwordState.alert}</FormAlert>
        <form onSubmit={savePassword} noValidate>
          <HiddenUsername username={user.username} />
          <Field
            label="Current password"
            type="password"
            value={passwords.current_password}
            onChange={(e) => setPasswords((p) => ({ ...p, current_password: e.target.value }))}
            autoComplete="current-password"
            required
          />
          <Field
            label="New password"
            type="password"
            value={passwords.new_password}
            onChange={(e) => setPasswords((p) => ({ ...p, new_password: e.target.value }))}
            hint="At least 12 characters. Your other sessions will be signed out."
            autoComplete="new-password"
            required
          />
          <button type="submit" className="btn btn-primary" disabled={passwordState.busy}>
            {passwordState.busy ? 'Saving…' : 'Change password'}
          </button>
        </form>
      </Section>

      <Section
        title="Two-factor authentication"
        description="Ask for a code from your phone as well as your password when you sign in."
      >
        {twoFactorFailed ? (
          <p className="form-alert error" role="alert">
            Your two-factor setting did not load.{' '}
            <button type="button" className="link-btn" onClick={loadTwoFactor}>
              Try again
            </button>
          </p>
        ) : twoFactor === null ? (
          <p className="subtle">Loading…</p>
        ) : !twoFactor.available && !twoFactor.enabled ? (
          // The flag gates *starting* two-factor, not managing one already
          // started. An enrolled account keeps the On row and Turn off while
          // `auth.totp` is off — otherwise the kill-switch strands the very
          // people it is being thrown for, who are still challenged at every
          // sign-in. `POST /me/two-factor/disable` is unflagged and works in
          // exactly this state. Set-up stays behind the flag: with nothing
          // enrolled this branch still catches it.
          <p className="subtle">This is not available on your account yet.</p>
        ) : (
          <>
            <FormAlert tone={twoFactorState.tone}>{twoFactorState.alert}</FormAlert>

            {recoveryCodes ? (
              <div className="mb-[18px]">
                <p className="row-title">Save these recovery codes</p>
                <p className="row-sub mb-2">
                  Each one signs you in once if you lose your phone, and using one turns two-factor
                  off. This is the only time they are shown.
                </p>
                <ol className="hairline-list num">
                  {recoveryCodes.map((recoveryCode) => (
                    <li className="row px-0" key={recoveryCode}>
                      {recoveryCode}
                    </li>
                  ))}
                </ol>
                <div className="mt-4">
                  <button type="button" className="btn btn-quiet" onClick={copyRecoveryCodes}>
                    {codesCopied ? 'Copied' : 'Copy all'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary ml-2"
                    onClick={() => {
                      setRecoveryCodes(null)
                      setCodesSaved(true)
                    }}
                  >
                    I have saved these
                  </button>
                </div>
              </div>
            ) : twoFactor.enabled ? (
              <>
                <div className="form-row p-0 mb-[18px]">
                  <div className="row-main">
                    <div className="row-title">On</div>
                    <div className="row-sub">
                      Since {new Date(twoFactor.enrolled_at).toLocaleDateString()} ·{' '}
                      {twoFactor.recovery_codes_remaining} recovery codes left
                      {codesSaved ? ' · codes saved' : ''}
                    </div>
                  </div>
                </div>
                <form onSubmit={turnOffTwoFactor} noValidate>
                  <HiddenUsername username={user.username} />
                  <Field
                    label="Current password"
                    type="password"
                    value={twoFactorForm.password}
                    onChange={(e) => setTwoFactorForm((f) => ({ ...f, password: e.target.value }))}
                    hint="Turning it off signs you in with your password alone again."
                    autoComplete="current-password"
                    required
                  />
                  <button type="submit" className="btn btn-quiet" disabled={twoFactorState.busy}>
                    {twoFactorState.busy ? 'Turning off…' : 'Turn off two-factor'}
                  </button>
                </form>
              </>
            ) : enrolment ? (
              <>
                <p className="row-sub mb-2">
                  Scan this with your authenticator app, or type the key in by hand.
                </p>
                <QrCode rows={enrolment.qr.rows} />
                <p className="row-title num my-2">{enrolment.secret}</p>
                <form onSubmit={confirmTwoFactor} noValidate>
                  <Field
                    label="Six-digit code"
                    value={twoFactorForm.code}
                    onChange={(e) => setTwoFactorForm((f) => ({ ...f, code: e.target.value }))}
                    hint="From the app, to prove the setup worked. Your other devices will be signed out."
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                  />
                  <button type="submit" className="btn btn-primary" disabled={twoFactorState.busy}>
                    {twoFactorState.busy ? 'Checking…' : 'Turn on two-factor'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet ml-2"
                    onClick={() => {
                      setEnrolment(null)
                      setTwoFactorForm({ password: '', code: '' })
                      setTwoFactorState({ alert: null, tone: 'caution', busy: false })
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </>
            ) : (
              <form onSubmit={startTwoFactor} noValidate>
                <HiddenUsername username={user.username} />
                <Field
                  label="Current password"
                  type="password"
                  value={twoFactorForm.password}
                  onChange={(e) => setTwoFactorForm((f) => ({ ...f, password: e.target.value }))}
                  hint="Asked for again so a borrowed session cannot enrol a phone you do not have."
                  autoComplete="current-password"
                  required
                />
                <button type="submit" className="btn btn-primary" disabled={twoFactorState.busy}>
                  {twoFactorState.busy ? 'Setting up…' : 'Set up two-factor'}
                </button>
              </form>
            )}
          </>
        )}
      </Section>

      <Section
        title="Invites"
        description="Codes you have issued. In the alpha the owner issues these."
      >
        {invitesFailed ? (
          <p className="form-alert error" role="alert">
            Your invites did not load.{' '}
            <button type="button" className="link-btn" onClick={loadInvites}>
              Try again
            </button>
          </p>
        ) : invites === null ? (
          <p className="subtle">Loading…</p>
        ) : invites.length === 0 ? (
          <p className="subtle">You have not issued any invites.</p>
        ) : (
          <div className="hairline-list">
            {invites.map((invite) => (
              <div className="row px-0" key={invite.id}>
                <div className="row-main">
                  <div className="row-title num">{invite.code}</div>
                  <div className="row-sub">{invite.note || 'No note'}</div>
                </div>
                <span className="state-chip">
                  {invite.revoked_at ? 'revoked' : `${invite.uses}/${invite.max_uses} used`}
                </span>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section
        title="What reaches you"
        description="Muting a type here does not tell anybody. It is a preference, not a block."
      >
        {/* The grid is hidden while the load has failed, because every toggle
            in it would read "on" — the lie this section is here to stop. */}
        {safetyFailed ? (
          <p className="form-alert error" role="alert">
            Your mute settings did not load.{' '}
            <button type="button" className="link-btn" onClick={loadSafety}>
              Try again
            </button>
          </p>
        ) : (
        <div className="mute-grid">
          <div className="mute-column">
            <h3 className="title-3">In your feed</h3>
            {FEED_EVENTS.map(([key, label]) => (
              <div className="form-row" key={`feed-${key}`}>
                <div className="row-main">
                  <div className="row-title">{label}</div>
                  <InlineAlert>{muteErrors[`feed:${key}`]}</InlineAlert>
                </div>
                <Toggle
                  checked={!(safety?.event_mutes?.feed || []).includes(key)}
                  onChange={(next) => setEventMute('feed', key, !next)}
                  label={`Show ${label} in your feed`}
                />
              </div>
            ))}
          </div>
          <div className="mute-column">
            <h3 className="title-3">In your notifications</h3>
            {NOTIFICATION_EVENTS.map(([key, label]) => (
              <div className="form-row" key={`notif-${key}`}>
                <div className="row-main">
                  <div className="row-title">{label}</div>
                  <InlineAlert>{muteErrors[`notification:${key}`]}</InlineAlert>
                </div>
                <Toggle
                  checked={!(safety?.event_mutes?.notification || []).includes(key)}
                  onChange={(next) => setEventMute('notification', key, !next)}
                  label={`Tell me about ${label}`}
                />
              </div>
            ))}
          </div>
        </div>
        )}
      </Section>

      <Section
        title="Blocked and muted"
        description="A block is symmetric — neither of you sees the other anywhere. A mute only quietens your feed, and the other person never learns of it."
      >
        {safetyFailed ? (
          <p className="form-alert error" role="alert">
            Your blocked and muted lists did not load.{' '}
            <button type="button" className="link-btn" onClick={loadSafety}>
              Try again
            </button>
          </p>
        ) : (
        <>
        <h3 className="title-3">Blocked</h3>
        {safety === null ? (
          <p className="subtle">Loading…</p>
        ) : safety.blocked.length === 0 ? (
          <p className="subtle">You have not blocked anybody.</p>
        ) : (
          <div className="hairline-list">
            {safety.blocked.map((person) => (
              <div className="row px-0" key={person.public_id}>
                <Avatar
                  name={person.display_name || person.username}
                  avatarUrl={person.avatar_url}
                  size={32}
                />
                <div className="row-main">
                  <div className="row-title">{person.display_name || person.username}</div>
                  <div className="row-sub">@{person.username}</div>
                  <InlineAlert>{safetyRowErrors[`blocked:${person.public_id}`]}</InlineAlert>
                </div>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() =>
                    safetyRowAction(
                      `blocked:${person.public_id}`,
                      () => api.unblock(person.username),
                      'Could not unblock this account just now.',
                    )
                  }
                >
                  Unblock
                </button>
              </div>
            ))}
          </div>
        )}

        <h3 className="title-3 mt-4">Muted</h3>
        {safety === null ? null : safety.muted.length === 0 ? (
          <p className="subtle">You have not muted anybody.</p>
        ) : (
          <div className="hairline-list">
            {safety.muted.map((person) => (
              <div className="row px-0" key={person.public_id}>
                <Avatar
                  name={person.display_name || person.username}
                  avatarUrl={person.avatar_url}
                  size={32}
                />
                <div className="row-main">
                  <div className="row-title">{person.display_name || person.username}</div>
                  <div className="row-sub">@{person.username}</div>
                  <InlineAlert>{safetyRowErrors[`muted:${person.public_id}`]}</InlineAlert>
                </div>
                <button
                  type="button"
                  className="btn btn-quiet btn-sm"
                  onClick={() =>
                    safetyRowAction(
                      `muted:${person.public_id}`,
                      () => api.unmute(person.username),
                      'Could not save that just now.',
                    )
                  }
                >
                  Unmute
                </button>
              </div>
            ))}
          </div>
        )}

        {safety?.follow_requests?.length ? (
          <>
            <h3 className="title-3 mt-4">Follow requests</h3>
            <div className="hairline-list">
              {safety.follow_requests.map((person) => (
                <div className="row px-0" key={person.public_id}>
                  <Avatar
                  name={person.display_name || person.username}
                  avatarUrl={person.avatar_url}
                  size={32}
                />
                  <div className="row-main">
                    <Link className="row-title" to={`/profile/${person.username}`}>
                      {person.display_name || person.username}
                    </Link>
                    <div className="row-sub">@{person.username}</div>
                    {/* One sentence for the pair: they sit side by side and
                        they answer the same request. */}
                    <InlineAlert>{safetyRowErrors[`request:${person.public_id}`]}</InlineAlert>
                  </div>
                  <button
                    type="button"
                    className="btn btn-tonal btn-sm"
                    onClick={() =>
                      safetyRowAction(
                        `request:${person.public_id}`,
                        () => api.respondToFollow(person.username, true),
                        'Could not answer that request just now.',
                      )
                    }
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn btn-quiet btn-sm"
                    onClick={() =>
                      safetyRowAction(
                        `request:${person.public_id}`,
                        () => api.respondToFollow(person.username, false),
                        'Could not answer that request just now.',
                      )
                    }
                  >
                    Decline
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : null}
        </>
        )}
      </Section>

      <Section
        title="Your data"
        description="A copy of everything Sonologue holds about you, and the way out."
      >
        {privacyError ? (
          <p className="form-alert error" role="alert">
            {privacyError}{' '}
            <button type="button" className="link-btn" onClick={loadPrivacy}>
              Try again
            </button>
          </p>
        ) : null}

        <div className="form-row p-0 mb-[18px]">
          <div className="row-main">
            <div className="row-title">Download your data</div>
            <div className="row-sub wrap">
              Your profile, ratings, reviews, lists, comments and follows, as JSON and CSV in one
              zip. It is built in the background; the link arrives by email and works for{' '}
              {privacy?.retention_days ?? 7} {plural(privacy?.retention_days ?? 7, 'day')}.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-tonal btn-sm"
            onClick={startExport}
            disabled={exporting || privacy?.exports?.some((e) => e.status === 'pending')}
          >
            {exporting ? 'Starting…' : 'Request a copy'}
          </button>
        </div>

        {privacy === null && !privacyError ? (
          <p className="subtle">Loading…</p>
        ) : privacy?.exports?.length ? (
          <div className="hairline-list">
            {privacy.exports.map((row) => (
              <div className="row px-0" key={row.public_id}>
                <div className="row-main">
                  <div className="row-title">{new Date(row.created_at).toLocaleString()}</div>
                  <div className="row-sub wrap">
                    {row.status === 'pending'
                      ? 'Being built. The worker picks this up within a few seconds.'
                      : row.status === 'ready'
                        ? `${Math.max(1, Math.round((row.byte_size || 0) / 1024))} KB · expires ${new Date(row.expires_at).toLocaleDateString()}`
                        : row.status === 'expired'
                          ? 'Expired and deleted.'
                          : row.error || 'That one failed. Ask for another.'}
                  </div>
                </div>
                {row.status === 'ready' ? (
                  <a className="btn btn-quiet btn-sm" href={api.exportDownloadUrl(row.public_id)}>
                    Download
                  </a>
                ) : (
                  <span className="state-chip">{row.status}</span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="subtle">You have not asked for a copy yet.</p>
        )}

        <hr className="hairline my-6" />

        <div className="form-row p-0 mb-[18px]">
          <div className="row-main">
            <div className="row-title">Delete all listening data</div>
            <div className="row-sub wrap">
              Clears every imported listen, your import history and your unrated queue. Ratings
              you made from the queue stay — they are opinions, not listens. Re-importing your
              export brings the listens back.
            </div>
            <InlineAlert>{wipeError}</InlineAlert>
          </div>
          {wiping.confirming ? (
            <div className="queue-actions">
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={wiping.busy}
                onClick={async () => {
                  setWipeError('')
                  setWiping({ confirming: true, busy: true, done: false })
                  try {
                    await api.deleteListeningData()
                    setWiping({ confirming: false, busy: false, done: true })
                  } catch (err) {
                    // Still confirming. Closing it here would leave exactly
                    // the state pressing *Keep* produces, which is how a
                    // refused wipe used to read as a cancelled one.
                    setWiping({ confirming: true, busy: false, done: false })
                    setWipeError(err instanceof ApiError ? err.message : 'That did not delete.')
                  }
                }}
              >
                {wiping.busy ? 'Deleting…' : 'Yes, delete it all'}
              </button>
              <button
                type="button"
                className="btn btn-quiet btn-sm"
                disabled={wiping.busy}
                onClick={() => setWiping({ confirming: false, busy: false, done: false })}
              >
                Keep
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-tonal btn-sm"
              onClick={() => setWiping({ confirming: true, busy: false, done: false })}
            >
              {wiping.done ? 'Deleted' : 'Delete listening data'}
            </button>
          )}
        </div>

        <div className="form-row p-0">
          <div className="row-main">
            <div className="row-title">Delete your account</div>
            <div className="row-sub wrap">
              Signs you out everywhere immediately and hides your account. Nothing is erased for{' '}
              {privacy?.grace_days ?? 14} {plural(privacy?.grace_days ?? 14, 'day')}, so you can
              change your mind — the link to do that
              is in the email we send.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => setDeleting({ open: true, mode: 'delete', password: '', busy: false, error: '' })}
          >
            Delete account
          </button>
        </div>
      </Section>

      <ConfirmDialog
        open={deleting.open}
        title="Delete your account"
        confirmLabel="Delete my account"
        confirmWord="delete my account"
        busy={deleting.busy}
        error={deleting.error}
        onConfirm={confirmDeletion}
        onClose={() => setDeleting((d) => ({ ...d, open: false }))}
      >
        <p>
          You will be signed out everywhere and your account will stop being visible. Everything
          is erased for good after {privacy?.grace_days ?? 14}{' '}
          {plural(privacy?.grace_days ?? 14, 'day')}. We will start a final copy of
          your data and email you the link before that happens.
        </p>

        <fieldset className="choice-set">
          <legend className="field-label">What happens to what you wrote</legend>
          {[
            [
              'delete',
              'Delete it',
              'Your reviews, comments and lists go too. Threads other people replied in keep a note that something was removed.',
            ],
            [
              'anonymize',
              'Leave it, without my name',
              'Your published reviews, comments and lists stay where they are, credited to "Deleted account".',
            ],
          ].map(([value, label, help]) => (
            <label className={`choice ${deleting.mode === value ? 'on' : ''}`} key={value}>
              <input
                type="radio"
                name="content-mode"
                value={value}
                checked={deleting.mode === value}
                onChange={() => setDeleting((d) => ({ ...d, mode: value }))}
              />
              <span className="row-main">
                <span className="row-title">{label}</span>
                <span className="row-sub wrap">{help}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <p className="footnote">
          Your ratings go on both paths — a score is one person&rsquo;s, and it leaves the
          community averages with you.
        </p>

        <HiddenUsername username={user.username} />
        <Field
          label="Your password"
          type="password"
          value={deleting.password}
          onChange={(event) => {
            const { value } = event.target
            setDeleting((d) => ({ ...d, password: value }))
          }}
          autoComplete="current-password"
        />
      </ConfirmDialog>

      <Section
        title="Signed-in devices"
        description="Everywhere your account is currently signed in. Ending one signs it out immediately."
      >
        <FormAlert tone="caution">{deviceState.error}</FormAlert>
        {devices === null ? (
          <p className="subtle">Loading…</p>
        ) : devices.length === 0 ? (
          <p className="subtle">Nothing else is signed in.</p>
        ) : (
          <>
            <div className="hairline-list">
              {devices.map((row) => (
                <div className="row px-0" key={row.handle}>
                  <div className="row-main">
                    <div className="row-title device-title">
                      <span className="device-name">{describeDevice(row.user_agent)}</span>
                      {row.is_current ? <span className="state-chip">this device</span> : null}
                    </div>
                    <div className="row-sub">
                      Last used {new Date(row.last_seen_at).toLocaleString()}
                      {row.ip_prefix ? ` · ${row.ip_prefix}` : ''}
                    </div>
                  </div>
                  {row.is_current ? null : (
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      onClick={() => endDevice(row.handle)}
                      disabled={deviceState.busy === row.handle}
                    >
                      {deviceState.busy === row.handle ? 'Ending…' : 'End'}
                    </button>
                  )}
                </div>
              ))}
            </div>
            {devices.length > 1 ? (
              <button
                type="button"
                className="btn btn-quiet mt-4"
                onClick={endOtherDevices}
                disabled={deviceState.busy === 'others'}
              >
                {deviceState.busy === 'others' ? 'Signing out…' : 'Sign out everywhere else'}
              </button>
            ) : null}
          </>
        )}
      </Section>

      <Section title="Session">
        <button type="button" className="btn btn-quiet" onClick={signOut}>
          Sign out
        </button>
      </Section>
    </>
  )
}
